import {
  ACTIVE_ORGANIZATION_ID_COOKIE,
  COOKIE_EXPIRY_DAYS,
} from '@lib/constants/cookies';
import Cookies from 'js-cookie';

const listeners = new Set<() => void>();
let tabActiveOrgId: string | undefined;

/** The raw active-org cookie; resolve it with `resolveActiveOrg` before use. */
export const readActiveOrgCookie = (): string | undefined =>
  Cookies.get(ACTIVE_ORGANIZATION_ID_COOKIE);

/**
 * The active-org cookie as this tab knows it: read when its first reader
 * subscribes, then changed only by this tab's own writes and the org a page
 * was rendered for (see `followRenderedActiveOrg`). The cookie is shared, and a
 * switch in another tab rewrites it without reloading this one; following it
 * would move this tab's header count and mini-cart to an org the page on
 * screen was not rendered for (CPS-7696).
 */
export const readTabActiveOrgCookie = (): string | undefined =>
  listeners.size > 0 ? tabActiveOrgId : readActiveOrgCookie();

/**
 * Writes the active-org cookie and tells subscribers, so readers outside
 * OrganizationsProvider (the header cart count) follow an org switch that does
 * not reload the page.
 */
export const writeActiveOrgCookie = (organizationId: string): void => {
  Cookies.set(ACTIVE_ORGANIZATION_ID_COOKIE, organizationId, {
    expires: COOKIE_EXPIRY_DAYS,
  });
  tabActiveOrgId = organizationId;
  listeners.forEach((listener) => listener());
};

/**
 * Makes the org a page was server-rendered for the one this tab works in, so
 * the header count and the mini-cart follow the page's entitlements and cart
 * (CPS-7696). A client navigation renders the new page for the cookie as it
 * is then, another tab's switch included, and the whole tab moves with it.
 */
export const followRenderedActiveOrg = (organizationId: string): void => {
  if (tabActiveOrgId === organizationId) return;
  tabActiveOrgId = organizationId;
  listeners.forEach((listener) => listener());
};

/** `useSyncExternalStore` subscribe for `readTabActiveOrgCookie`. */
export const subscribeToActiveOrgCookie = (
  listener: () => void
): (() => void) => {
  if (listeners.size === 0) tabActiveOrgId = readActiveOrgCookie();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
